import { JsonObject, JsonProperty } from "json2typescript";
import { SearchParams } from "../searchParams";
import { SearchResults } from "../searchResults";
import {
  ExpenseTypeEnum,
  TimesheetEntry,
  TimesheetExpense,
} from "../timesheet/timesheetEntry";
import * as converter from "number-to-words";
import { SequenceEnum } from "../cases/case";

export enum InvoiceStatus {
  Draft = 1,
  Issued = 2,
  Paid = 3,
}

@JsonObject("Invoice")
export class Invoice {
  @JsonProperty("id", Number, true)
  id: number = undefined;

  @JsonProperty("case_id", Number, true)
  case: number = undefined;

  @JsonProperty("invoice_num", String, true)
  invoiceNum: string = null;

  @JsonProperty("seq_number", String, true)
  seqNumber: string = null;

  @JsonProperty("client", String, true)
  client: string = null;

  @JsonProperty("client_ref", String, true)
  clientRef: string = null;

  @JsonProperty("subject", String, true)
  subject: string = null;

  @JsonProperty("our_ref", String, true)
  ourRef: string = null;

  @JsonProperty("currency", String, true)
  currency: string = null;

  @JsonProperty("currency_rate", Number, true)
  currencyRate: number = undefined;

  @JsonProperty("financial_contact", String, true)
  financialContact: string = null;

  @JsonProperty("address_street", String, true)
  addressStreet: string = null;

  @JsonProperty("address_city", String, true)
  addressCity: string = null;

  @JsonProperty("address_country", String, true)
  addressCountry: string = null;

  @JsonProperty("phone", String, true)
  phone: string = null;

  @JsonProperty("email", String, true)
  email: string = null;

  @JsonProperty("vat", Number, true)
  vat: number = 14;

  @JsonProperty("lumpsum", Number, true)
  lumpsum: number = undefined;

  @JsonProperty("total_amount", Number, true)
  totalAmount: number = undefined;

  @JsonProperty("lumpsum_description", String, true)
  lumpsumDescription: string = null;

  @JsonProperty("status", Number, true)
  status: InvoiceStatus = InvoiceStatus.Draft;

  @JsonProperty("invoice_date", String, true)
  invoiceDate: string = undefined;

  @JsonProperty("from_date", String, true)
  dateFrom: string = undefined;

  @JsonProperty("to_date", String, true)
  dateTo: string = undefined;

  @JsonProperty("timesheet_entries", [TimesheetEntry], true)
  timesheetEntries: TimesheetEntry[] = [];

  @JsonProperty("timesheet_expenses", [TimesheetExpense], true)
  timesheetExpenses: TimesheetExpense[] = [];

  @JsonProperty("entries", [Number], true)
  entries: number[] = [];

  @JsonProperty("expenses", [Number], true)
  expenses: number[] = [];

  @JsonProperty("case_sequence", Number, true)
  caseSequence: SequenceEnum = null;

  showInvoicePeriod = true;
  showCurrencyExchangeNote = false;
  showLumpsumDescription = false;

  get totalProfCharges() {
    return this.timesheetEntries
      .map((m) => m.amount)
      .reduce((sum, current) => sum + current, 0);
  }

  get totalDisbursements() {
    return this.timesheetExpenses
      .filter((f) => f.expenseType === ExpenseTypeEnum.DISBURSEMENTS)
      .map((m) => m.amount)
      .reduce((sum, current) => sum + current, 0);
  }

  get total3rdPartyFees() {
    return this.timesheetExpenses
      .filter((f) => f.expenseType === ExpenseTypeEnum["THIRD PARTY FEES"])
      .map((m) => m.amount)
      .reduce((sum, current) => sum + current, 0);
  }

  get total3rdPartyDisbursements() {
    return this.timesheetExpenses
      .filter(
        (f) => f.expenseType === ExpenseTypeEnum["THIRD PARTY DISBURSEMENTS"]
      )
      .map((m) => m.amount)
      .reduce((sum, current) => sum + current, 0);
  }

  vatAmount() {
    return this.totalProfCharges * (this.vat / 100);
  }

  get total() {
    return this.lumpsum || this.totalAmount;
  }

  get isDraft() {
    return this.status === InvoiceStatus.Draft;
  }

  get isPaid() {
    return this.status === InvoiceStatus.Paid;
  }

  get isIssued() {
    return this.status === InvoiceStatus.Issued;
  }

  get statusString() {
    return InvoiceStatus[this.status];
  }

  get invoiceTitle() {
    return this.id
      ? this.isDraft
        ? `File# ${this.seqNumber} – Invoice Summary (Draft)`
        : `Invoice# ${this.invoiceNum}`
      : "Draft New Invoice";
  }

  get address() {
    return (
      this.addressStreet + ", " + this.addressCity + ", " + this.addressCountry
    );
  }

  get totalToWords() {
    return converter.toWords(this.total || 0);
  }

  get isPandi() {
    return [SequenceEnum.Pandi, SequenceEnum.Sudan].includes(this.caseSequence);
  }
}

@JsonObject("InvoiceSearchParams")
export class InvoiceSearchParams extends SearchParams {
  @JsonProperty("case", [Number], true)
  case: number[] = [];

  @JsonProperty("status", [Number], true)
  status: number[] = [];

  @JsonProperty("invoice_date_from", Date, true)
  invoiceDateFrom: Date;

  @JsonProperty("invoice_date_tp", Date, true)
  invoiceDateTo: Date;
}

@JsonObject("InvoiceSearchResults")
export class InvoiceSearchResults extends SearchResults {
  @JsonProperty("results", [Invoice])
  results: Invoice[] = [];
}
